import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BlockRenderer } from '@/components/blocks/BlockRenderer'
import { Modal } from '@/components/ui/Modal'
import { Tag } from '@/components/ui/Tag'
import { getProject } from '@/content/projects'
import styles from './ProjectModal.module.css'

/**
 * Renders the project at `/projects/:slug` as a dialog over the grid.
 * Closing navigates back, so the browser's back button and the close button do
 * exactly the same thing.
 */
export function ProjectModal() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const project = getProject(slug)

  const close = useCallback(() => {
    navigate('/projects')
  }, [navigate])

  if (!project) {
    return (
      <Modal open onClose={close} title="Project not found">
        <p>
          There is no project at that address. It may have been renamed — try the
          project list.
        </p>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onClose={close}
      title={project.title}
      eyebrow={
        <>
          {project.period && <span>{project.period}</span>}
          <ul className={styles.tags}>
            {project.tags.map((tag) => (
              <li key={tag}>
                <Tag name={tag} size="sm" />
              </li>
            ))}
          </ul>
        </>
      }
      actions={project.links?.map((link) => (
        <a
          key={link.href}
          className={styles.action}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.label} ↗
        </a>
      ))}
    >
      <BlockRenderer blocks={project.blocks} />
    </Modal>
  )
}
